import { AgentStatus } from '@21k/shared';
import { useEffect, useRef, useState } from 'react';

import {
  getApiErrorMessage,
  getMyAgentStatus,
  updateMyAgentStatus,
} from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { normalizeAgentStatus, STATUS_CONFIG } from '../utils/agentStatus';

export default function AgentStatusDropdown() {
  const { user } = useAuth();
  const { socket } = useSocket();
  const { showToast } = useToast();
  const [agentStatus, setAgentStatus] = useState<AgentStatus>(AgentStatus.LIVE);
  const [statusOpen, setStatusOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getMyAgentStatus()
      .then((data) => setAgentStatus(normalizeAgentStatus(data.agentStatus)))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!user) return;
    setAgentStatus(normalizeAgentStatus(user.agentStatus));
  }, [user?.agentStatus, user]);

  useEffect(() => {
    if (!socket || !user) return;

    const onStatusChanged = ({
      agentId,
      status,
    }: {
      agentId: string;
      status: AgentStatus;
    }) => {
      if (agentId === user.id) {
        setAgentStatus(normalizeAgentStatus(status));
      }
    };

    socket.on('agent:status_changed', onStatusChanged);
    return () => {
      socket.off('agent:status_changed', onStatusChanged);
    };
  }, [socket, user]);

  useEffect(() => {
    if (!statusOpen) return;

    function onMouseDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setStatusOpen(false);
      }
    }

    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [statusOpen]);

  async function handleStatusChange(status: AgentStatus) {
    setStatusOpen(false);
    const prev = agentStatus;
    setAgentStatus(status);

    try {
      await updateMyAgentStatus(status);
    } catch (err) {
      setAgentStatus(prev);
      showToast({
        title: 'Failed to update status',
        label: getApiErrorMessage(err),
      });
    }
  }

  const cfg = STATUS_CONFIG[agentStatus];

  return (
    <div className="agent-status-dropdown" ref={rootRef}>
      <button
        type="button"
        className="agent-status-trigger"
        onClick={() => setStatusOpen((prev) => !prev)}
        aria-expanded={statusOpen}
        aria-haspopup="listbox"
      >
        <span className="agent-status-trigger-dot" style={{ color: cfg.color }} aria-hidden>
          {cfg.dot}
        </span>
        <span className="agent-status-trigger-label">{cfg.label}</span>
        <span className="agent-status-trigger-caret" aria-hidden>▾</span>
      </button>

      {statusOpen && (
        <div className="agent-status-menu" role="listbox" aria-label="Agent availability">
          {(Object.entries(STATUS_CONFIG) as [AgentStatus, typeof cfg][]).map(([key, item]) => (
            <button
              key={key}
              type="button"
              role="option"
              aria-selected={agentStatus === key}
              className={`agent-status-menu-item${agentStatus === key ? ' is-active' : ''}`}
              onClick={() => void handleStatusChange(key)}
            >
              <span className="agent-status-menu-dot" style={{ color: item.color }} aria-hidden>
                {item.dot}
              </span>
              {item.label}
              {agentStatus === key && (
                <span className="agent-status-menu-check" aria-hidden>✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
